import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import { DispatchBoardQueueLoad, TrailerSize, getTrailerSizeDisplayLabel } from "app/data/city-delivery/dispatch-board/models";
import { useDispatchManifest } from "features/city-delivery/dispatch-board/hooks/useDoor";
import UserState from "slices/user/UserState";
import { userSelector } from "slices/user/userSlice";
import SlideOutSidebar from "ui-components/slide-out-sidebar/slideOutSidebar";
import CarrierService from "app/data/carriers/carriersServices";
import DriverService from "app/data/drivers/driversService";
import { DriverItem } from "app/data/drivers/models";
import customXGSSelectStyles from "ui-components/xgs-select/customXGSSelectStyles";
import Button, { ButtonThemes } from "ui-components/button/button";
import "ui-components/xgs-select/xgsSelect.scss";
import "./dispatchManifest.scss";
import { toast } from "react-toastify";

const TRAILER_SIZE_OPTIONS = Object.values(TrailerSize).map((value) => ({
  value,
  label: getTrailerSizeDisplayLabel(value),
}));

export interface DriverOption {
  value: number;
  label: string;
  driverName?: string;
  driverId?: string;
  errorMessage?: string;
  isDisabled?: boolean;
}

export interface CarrierOption {
  value: number;
  label: string;
}

export interface DispatchManifestProps {
  show: boolean;
  onClose: () => void;
  dispatchLoad: DispatchBoardQueueLoad;
}

const carrierService = CarrierService.getInstance();
const driverService = DriverService.getInstance();

const ACTIVE_STATUS = "active";

function getDriverErrorMessage(
  driver: DriverItem,
  activeTerminalId: string | undefined
): string | undefined {
  if (!activeTerminalId) return undefined;
  const driverTerminal = driver.domicileTerminalNumber?.toString().trim() ?? "";
  const terminalMatch =
    driverTerminal === "" || driverTerminal === activeTerminalId.toString().trim();
  if (!terminalMatch) {
    return "Assigned to different terminal";
  }
  if (driver.status?.toLowerCase() !== ACTIVE_STATUS) {
    return "Status not active";
  }
  return undefined;
}

const loadDriverOptions = async (
  inputValue: string,
  activeTerminalId: string | undefined
): Promise<DriverOption[]> => {
  if (!inputValue || inputValue.trim().length < 2) return [];
  const response = await driverService.getDrivers(
    "", // search all terminals
    inputValue.trim(),
    0
  );
  const items: DriverItem[] = response?.data?.items ?? [];
  return items.map((d) => {
    const driverName = `${d.firstName ?? ""} ${d.lastName ?? ""}`.trim();
    const errorMessage = getDriverErrorMessage(d, activeTerminalId);
    const isDisabled = !!errorMessage;
    return {
      value: +d.driverId,
      label: `${driverName} (${d.driverId})`,
      driverName: driverName || d.driverId,
      driverId: d.driverId,
      errorMessage,
      isDisabled,
    };
  });
};

const loadCarrierOptions = async (inputValue: string): Promise<CarrierOption[]> => {
  if (!inputValue || inputValue.trim().length < 2) return [];
  const response = await carrierService.getcarriersList(inputValue.trim(), 0);
  const content = response?.data?.content ?? [];
  return content.map((c) => ({
    value: c.carrierNumber,
    label: `${c.carrierName} (${c.carrierNumber})`,
  }));
};

const debounce = <T extends (...args: any[]) => any>(fn: T, ms: number) => {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    return new Promise<ReturnType<T>>((resolve) => {
      timeoutId = setTimeout(() => resolve(fn(...args)), ms);
    });
  };
};

function getInitialDriverFromDispatchLoad(
  load: DispatchBoardQueueLoad
): DriverOption | null {
  if (load?.driverNumber == null) return null;
  const label = load.driverName
    ? `${load.driverName} (${load.driverNumber})`
    : String(load.driverNumber);
  return { value: load.driverNumber, label };
}

function getInitialCarrierFromDispatchLoad(
  load: DispatchBoardQueueLoad
): CarrierOption | null {
  if (load?.carrierNumber == null) return null;
  const label = load.carrierName
    ? `${load.carrierName} (${load.carrierNumber})`
    : String(load.carrierNumber);
  return { value: load.carrierNumber, label };
}

export const DispatchManifest: React.FC<DispatchManifestProps> = (props) => {
  const { show, onClose, dispatchLoad } = props;
  const [selectedDriver, setSelectedDriver] = useState<DriverOption | null>(null);
  const [selectedCarrier, setSelectedCarrier] = useState<CarrierOption | null>(null);
  const [tractorNumber, setTractorNumber] = useState("");
  const [trailerNumber, setTrailerNumber] = useState("");
  const [trailerSize, setTrailerSize] = useState<{ value: TrailerSize; label: string } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const userState: UserState = useSelector(userSelector);
  const terminalNumber = userState.activeTerminal?.id;

  useEffect(() => {
    if (show && dispatchLoad) {
      setSelectedDriver(getInitialDriverFromDispatchLoad(dispatchLoad));
      setSelectedCarrier(getInitialCarrierFromDispatchLoad(dispatchLoad));
      setTractorNumber(dispatchLoad.tractorNumber ?? "");
      setTrailerNumber(dispatchLoad.trailerNumber ?? "");
      setTrailerSize(null);
      setErrors({});
    }
  }, [show, dispatchLoad]);

  const handleSuccess = useCallback(() => {
    toast.info("Dispatch manifest submitted successfully");
    onClose();
  }, [onClose]);

  const handleError = useCallback((e: any) => {
    const msg =
    e?.response?.data?.detail ?? e?.data?.detail ?? "Failed to submit dispatch manifest";
    toast.error(msg);
  }, []);

  const dispatchManifestMutation = useDispatchManifest(handleSuccess, handleError);

  const activeTerminalId = userState.activeTerminal?.id?.toString() ?? "";

  const loadDriverOptionsDebounced = useMemo(
    () =>
      debounce(
        (input: string) => loadDriverOptions(input, activeTerminalId),
        300
      ),
    [activeTerminalId]
  );

  const loadCarrierOptionsDebounced = useMemo(
    () => debounce(loadCarrierOptions, 300),
    []
  );

  const validate = useCallback((): boolean => {
    const next: Record<string, string> = {};
    if (!selectedDriver) next.driver = "Driver is required";
    if (!selectedCarrier) next.carrier = "Carrier is required";
    if (!tractorNumber.trim()) next.tractor = "Tractor is required";
    if (!trailerNumber.trim()) next.trailer = "Trailer is required";
    if (!trailerSize) next.trailerSize = "Trailer size is required";
    setErrors(next);
    return Object.keys(next).length === 0;
  }, [
    selectedDriver,
    selectedCarrier,
    tractorNumber,
    trailerNumber,
    trailerSize,
  ]);

  const handleSubmit = useCallback(() => {
    if (!validate()) return;
    if (!terminalNumber || !dispatchLoad?.manifestNumber) return;
    dispatchManifestMutation.mutate({
      terminalNumber,
      manifestNumber: dispatchLoad.manifestNumber,
      driverNumber: selectedDriver?.value,
      carrierNumber: selectedCarrier?.value,
      tractorNumber: tractorNumber.trim(),
      trailerNumber: trailerNumber.trim(),
      size: trailerSize?.value,
    });
  }, [
    validate,
    terminalNumber,
    dispatchLoad?.manifestNumber,
    selectedDriver,
    selectedCarrier,
    tractorNumber,
    trailerNumber,
    trailerSize,
    dispatchManifestMutation,
  ]);

  const isSubmitDisabled = dispatchManifestMutation.isLoading;

  return (
    <SlideOutSidebar
      header="Dispatch Manifest"
      show={show}
      onClose={onClose}
      size="small"
    >
      <div className="dispatch-manifest-form">
        <div className="dispatch-manifest-form__section">
          <label className="dispatch-manifest-form__label">
            Driver <span className="dispatch-manifest-form__required">*</span>
          </label>
          <AsyncSelect<DriverOption>
            placeholder="Search driver by name or ID..."
            loadOptions={loadDriverOptionsDebounced}
            value={selectedDriver}
            onChange={(val) => {
              const option = Array.isArray(val) ? null : val ?? null;
              setSelectedDriver(option as DriverOption | null);
              if (errors.driver) setErrors((prev) => ({ ...prev, driver: "" }));
            }}
            isClearable
            defaultOptions={[]}
            noOptionsMessage={({ inputValue }) =>
              !inputValue || inputValue.length < 2
                ? "Type at least 2 characters to search"
                : "No drivers found"
            }
            isOptionDisabled={(option) => option.isDisabled === true}
            formatOptionLabel={(option, { context }) => {
              const name = option.driverName ?? option.label;
              const id = option.driverId ?? "";
              const showError = context === "menu" && option.errorMessage;
              return (
                <div className="dispatch-manifest-form__driver-option">
                  <div className="dispatch-manifest-form__driver-option-main">
                    {name} {id && `(${id})`}
                  </div>
                  {showError && (
                    <span className="dispatch-manifest-form__driver-option-error-tag">
                      {option.errorMessage}
                    </span>
                  )}
                </div>
              );
            }}
            styles={customXGSSelectStyles}
            classNamePrefix="xgs-select"
            components={{ IndicatorSeparator: () => null }}
          />
          {errors.driver && (
            <span className="dispatch-manifest-form__error-tag">{errors.driver}</span>
          )}
        </div>

        <div className="dispatch-manifest-form__section">
          <label className="dispatch-manifest-form__label">
            Carrier <span className="dispatch-manifest-form__required">*</span>
          </label>
          <AsyncSelect<CarrierOption>
            placeholder="Search carrier by name or number..."
            loadOptions={loadCarrierOptionsDebounced}
            value={selectedCarrier}
            onChange={(val) => {
              const option = Array.isArray(val) ? null : val ?? null;
              setSelectedCarrier(option as CarrierOption | null);
              if (errors.carrier) setErrors((prev) => ({ ...prev, carrier: "" }));
            }}
            isClearable
            defaultOptions={[]}
            noOptionsMessage={({ inputValue }) =>
              !inputValue || inputValue.length < 2
                ? "Type at least 2 characters to search"
                : "No carriers found"
            }
            styles={customXGSSelectStyles}
            classNamePrefix="xgs-select"
            components={{ IndicatorSeparator: () => null }}
          />
          {errors.carrier && (
            <span className="dispatch-manifest-form__error-tag">{errors.carrier}</span>
          )}
        </div>

        <div className="dispatch-manifest-form__row">
          <div className="dispatch-manifest-form__section">
            <label className="dispatch-manifest-form__label" htmlFor="dm-tractor">
              Tractor <span className="dispatch-manifest-form__required">*</span>
            </label>
            <input
              id="dm-tractor"
              type="text"
              className={`dispatch-manifest-form__input ${errors.tractor ? "dispatch-manifest-form__input--error" : ""}`}
              value={tractorNumber}
              onChange={(e) => {
                setTractorNumber(e.target.value);
                if (errors.tractor) setErrors((prev) => ({ ...prev, tractor: "" }));
              }}
              placeholder="Enter tractor number"
            />
            {errors.tractor && (
              <span className="dispatch-manifest-form__error-tag">{errors.tractor}</span>
            )}
          </div>
          <div className="dispatch-manifest-form__section">
            <label className="dispatch-manifest-form__label" htmlFor="dm-trailer">
              Trailer <span className="dispatch-manifest-form__required">*</span>
            </label>
            <input
              id="dm-trailer"
              type="text"
              className={`dispatch-manifest-form__input ${errors.trailer ? "dispatch-manifest-form__input--error" : ""}`}
              value={trailerNumber}
              onChange={(e) => {
                setTrailerNumber(e.target.value);
                if (errors.trailer) setErrors((prev) => ({ ...prev, trailer: "" }));
              }}
              placeholder="Enter trailer number"
            />
            {errors.trailer && (
              <span className="dispatch-manifest-form__error-tag">{errors.trailer}</span>
            )}
          </div>
        </div>

        <div className="dispatch-manifest-form__section">
          <label className="dispatch-manifest-form__label">
            Trailer size <span className="dispatch-manifest-form__required">*</span>
          </label>
          <Select<{ value: TrailerSize; label: string }>
            placeholder="Select trailer size"
            options={TRAILER_SIZE_OPTIONS}
            value={trailerSize}
            onChange={(val) => {
              const option = Array.isArray(val) ? null : val ?? null;
              setTrailerSize(option as { value: TrailerSize; label: string } | null);
              if (errors.trailerSize) setErrors((prev) => ({ ...prev, trailerSize: "" }));
            }}
            isClearable
            styles={customXGSSelectStyles}
            classNamePrefix="xgs-select"
            components={{ IndicatorSeparator: () => null }}
          />
          {errors.trailerSize && (
            <span className="dispatch-manifest-form__error-tag">{errors.trailerSize}</span>
          )}
        </div>

        <div className="dispatch-manifest-form__actions">
          <Button
            theme={ButtonThemes.blue}
            className="dispatch-manifest-form__submit-btn"
            disabled={isSubmitDisabled}
            spinner={dispatchManifestMutation.isLoading}
            onClick={handleSubmit}
          >
            Dispatch Manifest
          </Button>
        </div>
      </div>
    </SlideOutSidebar>
  );
};
